import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import ScrollArrow from '../components/ScrollArrow'
import GlareCard from '../components/ui/GlareCard'
import ProgressiveImage from '../components/ui/ProgressiveImage'
import { textReveal, staggerContainer, staggerItem } from '../animations/variants'

const Mission = () => {
  const { t } = useLanguage()

  return (
    <div className="bg-black">
      {/* Hero Section - Full Screen */}
      <section className="section-fullscreen">
        <div className="section-bg">
          <ProgressiveImage
            src="/images/hero-mission.jpg"
            placeholder="/images/placeholders/hero-mission-placeholder.jpg"
            alt="Manufacturing"
          />
          <div className="section-overlay" />
          <div className="section-overlay-left" />
        </div>

        <div className="section-content">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={textReveal} className="section-label">OUR MISSION</motion.div>
            <motion.h1 variants={textReveal} className="section-heading text-glow">
              {t('mission.hero.title')}
            </motion.h1>
            <motion.p variants={textReveal} className="text-xl md:text-2xl font-mono text-gray-300 mb-6">
              {t('mission.hero.subtitle')}
            </motion.p>
            <motion.p variants={textReveal} className="section-description">
              {t('mission.hero.content')}
            </motion.p>
          </motion.div>
        </div>

        <ScrollArrow />
      </section>

      {/* Mission Implementation - 网格纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-grid-texture">
        {/* 顶部渐变过渡 */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black to-transparent pointer-events-none" />
        {/* 底部渐变过渡 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">IMPLEMENTATION</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('mission.implementation.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm max-w-3xl mx-auto">
              {t('mission.implementation.subtitle')}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'memory',
                title: t('mission.platform.title'),
                features: [t('mission.platform.feature1'), t('mission.platform.feature2'), t('mission.platform.feature3')]
              },
              {
                icon: 'cloud',
                title: t('mission.toolchain.title'),
                features: [t('mission.toolchain.feature1'), t('mission.toolchain.feature2'), t('mission.toolchain.feature3')]
              },
              {
                icon: 'code',
                title: t('mission.ecosystem.title'),
                features: [t('mission.ecosystem.feature1'), t('mission.ecosystem.feature2'), t('mission.ecosystem.feature3')]
              }
            ].map((item, index) => (
              <GlareCard key={index}>
                <span className="material-symbols-outlined text-3xl text-white/30 mb-4">{item.icon}</span>
                <h3 className="section-title mb-6">{item.title}</h3>
                <ul className="space-y-3">
                  {item.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-1 h-1 bg-white/50 rounded-full mt-2"></span>
                      <span className="text-gray-500 text-sm font-mono">{feature}</span>
                    </li>
                  ))}
                </ul>
              </GlareCard>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Benefits - 渐变纹理背景 */}
      <section className="relative z-10 py-24 px-6 md:px-20 bg-gradient-texture">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div variants={textReveal} className="section-label justify-center flex mb-6">VALUE PROPOSITION</motion.div>
            <motion.h2 variants={textReveal} className="text-3xl md:text-4xl font-bold text-white mb-6 uppercase tracking-[-0.02em]">
              {t('mission.value.title')}
            </motion.h2>
            <motion.p variants={textReveal} className="text-gray-400 font-mono text-sm">
              {t('mission.value.subtitle')}
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <motion.div variants={staggerItem} className="metal-surface p-8">
              <span className="material-symbols-outlined text-2xl text-white/30 mb-4">target</span>
              <h3 className="section-title mb-4">{t('mission.value.innovation.title')}</h3>
              <ul className="space-y-3">
                {[
                  t('mission.value.innovation.feature1'),
                  t('mission.value.innovation.feature2'),
                  t('mission.value.innovation.feature3')
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-1 h-1 bg-white/50 rounded-full mt-2"></span>
                    <span className="text-gray-400 text-sm font-mono">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div variants={staggerItem} className="metal-surface p-8">
              <span className="material-symbols-outlined text-2xl text-white/30 mb-4">build</span>
              <h3 className="section-title mb-4">{t('mission.value.efficiency.title')}</h3>
              <ul className="space-y-3">
                {[
                  t('mission.value.efficiency.feature1'),
                  t('mission.value.efficiency.feature2'),
                  t('mission.value.efficiency.feature3')
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-1 h-1 bg-white/50 rounded-full mt-2"></span>
                    <span className="text-gray-400 text-sm font-mono">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action - 深灰背景 */}
      <section className="relative z-10 py-32 px-6 md:px-20 bg-dark-gray">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={staggerContainer}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={textReveal} className="section-label justify-center flex mb-6">GET STARTED</motion.div>
          <motion.h2 variants={textReveal} className="text-4xl md:text-5xl font-bold text-white mb-8 uppercase tracking-[-0.02em]">
            {t('mission.cta.title')}
          </motion.h2>
          <motion.p variants={textReveal} className="text-gray-500 text-lg font-mono mb-12">
            {t('mission.cta.subtitle')}
          </motion.p>
          <motion.div variants={textReveal} className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/products" className="section-cta group">
              <span>{t('mission.cta.products')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/careers" className="section-cta group border-white/10 hover:border-white/30 hover:bg-transparent hover:text-white">
              <span>{t('mission.cta.careers')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  )
}

export default Mission
